import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { formatINR, statusTone } from '@pmc/shared';
import { PMC, tint } from '../theme';

/**
 * Small shared building blocks. Kept together because each is a handful of
 * lines and they are almost always imported as a group.
 */

type StatusTone = 'success' | 'warning' | 'error' | 'info';

/**
 * Bill / transaction / application status, coloured by meaning.
 *
 * `label`/`tone` let a caller override the display text or colour for a
 * specific meaning of a raw status value (e.g. a CANCELLED bill that was
 * actually carried forward into a new one) without changing what
 * `statusTone()` returns for that status everywhere else it's used.
 */
export function StatusChip({
  status,
  label,
  tone: toneOverride,
}: {
  status: string;
  label?: string;
  tone?: StatusTone;
}) {
  const tone = toneOverride ?? statusTone(status);
  const colours = {
    success: { fg: PMC.success, bg: PMC.successBg },
    warning: { fg: PMC.warning, bg: PMC.warningBg },
    error: { fg: PMC.danger, bg: PMC.dangerBg },
    info: { fg: PMC.primary, bg: PMC.primaryLight },
  }[tone];

  return (
    <Chip
      label={label ?? status}
      size="small"
      sx={{ color: colours.fg, backgroundColor: colours.bg, border: 'none' }}
    />
  );
}

export function billStatusLabel(
  status: string,
  regularized = false,
  carriedForward = false,
  paidInFull = false,
  ledgerSettled = false,
): string {
  if (paidInFull) return 'PAID';
  if (ledgerSettled) return 'Settled — No Dues';
  if (regularized) return 'Bill Regularized';
  return status === 'CANCELLED' && carriedForward ? 'Bill Carried Forward' : status;
}

export function billStatusTone(
  status: string,
  regularized = false,
  carriedForward = false,
  paidInFull = false,
  ledgerSettled = false,
): StatusTone | undefined {
  if (paidInFull || ledgerSettled) return 'success';
  if (regularized) return 'info';
  return status === 'CANCELLED' && carriedForward ? 'info' : undefined;
}

/** Rupee amount with tabular figures so columns align. */
export function Money({
  value,
  variant = 'body2',
  bold = false,
  color,
}: {
  value: number;
  variant?: 'body1' | 'body2' | 'h3' | 'h4' | 'caption';
  bold?: boolean;
  color?: string;
}) {
  return (
    <Typography
      component="span"
      variant={variant}
      className="num"
      sx={{ fontWeight: bold ? 700 : 500, color, whiteSpace: 'nowrap' }}
    >
      {formatINR(value)}
    </Typography>
  );
}

/** Headline figure tile for dashboards. */
export function StatCard({
  label,
  value,
  hint,
  tone = PMC.primary,
  icon,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: string;
  icon?: ReactNode;
}) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          {icon ? (
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                backgroundColor: tint(tone, 0.1),
                color: tone,
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          ) : null}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.25 }}>
              {label}
            </Typography>
            <Typography variant="h4" className="num" sx={{ color: tone, lineHeight: 1.2 }}>
              {value}
            </Typography>
            {hint ? (
              <Typography variant="caption" color="text.secondary">
                {hint}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      spacing={1.5}
      sx={{ mb: 2.5 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h2" sx={{ mb: subtitle ? 0.5 : 0 }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {action}
    </Stack>
  );
}

const SECTION_TONES = {
  primary: PMC.primary,
  navy: PMC.navy,
} as const;

export function SectionCard({
  title,
  icon,
  tone = 'primary',
  action,
  children,
  dense = false,
}: {
  title?: string;
  icon?: ReactNode;
  tone?: keyof typeof SECTION_TONES;
  action?: ReactNode;
  children: ReactNode;
  dense?: boolean;
}) {
  const toneColor = SECTION_TONES[tone];

  return (
    <Card>
      {title ? (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2.25,
            py: 1.5,
            borderBottom: `1px solid ${icon ? tint(toneColor, 0.22) : PMC.line}`,
            backgroundColor: icon ? tint(toneColor, 0.14) : undefined,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            {icon ? (
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  backgroundColor: toneColor,
                  color: '#fff',
                  flexShrink: 0,
                  '& svg': { fontSize: 17 },
                }}
              >
                {icon}
              </Box>
            ) : null}
            <Typography variant="h5" sx={icon ? { color: toneColor } : undefined}>
              {title}
            </Typography>
          </Stack>
          {action}
        </Stack>
      ) : null}
      <Box sx={{ p: dense ? 0 : 2.25 }}>{children}</Box>
    </Card>
  );
}

export function EmptyState({ message, action }: { message: string; action?: ReactNode }) {
  return (
    <Stack alignItems="center" spacing={1.5} sx={{ py: 5, px: 2, textAlign: 'center' }}>
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
      {action}
    </Stack>
  );
}

export function ErrorState({ message }: { message: string }) {
  return (
    <Alert severity="error" sx={{ my: 2 }}>
      {message}
    </Alert>
  );
}

export function LoadingBlock({ height = 180 }: { height?: number }) {
  return <Skeleton variant="rounded" height={height} sx={{ borderRadius: 2 }} />;
}

export function LoadingPage() {
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '50vh' }}>
      <CircularProgress />
    </Stack>
  );
}

export function TableSkeleton({ rows = 5, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <Box sx={{ p: 2 }}>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <Stack key={rowIndex} direction="row" spacing={2} sx={{ mb: 1.25 }}>
          {Array.from({ length: columns }).map((__, colIndex) => (
            <Skeleton key={colIndex} variant="text" sx={{ flex: colIndex === 0 ? 1.6 : 1 }} />
          ))}
        </Stack>
      ))}
    </Box>
  );
}

export function KeyValue({
  label,
  value,
  strong = false,
  align = 'right',
  labelWidth,
}: {
  label: string;
  value: ReactNode;
  strong?: boolean;
  align?: 'left' | 'right';
  labelWidth?: number;
}) {
  return (
    <Stack
      direction="row"
      justifyContent={align === 'right' ? 'space-between' : 'flex-start'}
      alignItems="baseline"
      spacing={2}
      sx={{ py: 0.65, borderBottom: `1px dashed ${PMC.line}` }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ flexShrink: 0, minWidth: align === 'left' ? (labelWidth ?? 130) : 'auto' }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: strong ? 700 : 500, textAlign: align, wordBreak: 'break-word' }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

export function PageBackground({
  children,
  maxWidth = 'lg',
}: {
  children: ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
}) {
  return (
    <Container maxWidth={maxWidth} sx={{ py: 3 }}>
      {children}
    </Container>
  );
}

